import React, { FC } from 'react';
import ThreeStateCheckbox, { ThreeStateValueType } from 'src/components/ThreeStateCheckbox';
import { getChildKeys, TreeNodeType } from '.';

export type SelectNodeValueType = [number, number]; //[selected, missing]
export type SelectedNodesType = { [key: string]: SelectNodeValueType };
export type SelectedItemsType = { [key: string]: boolean };
export type DisableItemSelectType = (key: string) => boolean;

const recursiveSelect = (
    value: ThreeStateValueType,
    treeKey: string,
    treeNode: TreeNodeType,
    newSelected: SelectedItemsType
) => {
    if (treeNode.isEndpoint && treeNode.data) {
        onSelectEndpoint(value, treeKey, treeNode, newSelected);
    }
    getChildKeys(treeNode).forEach((key) => recursiveSelect(value, treeKey, treeNode[key], newSelected));
};

const onSelectNode = (
    value: ThreeStateValueType,
    treeKey: string,
    treeNode: TreeNodeType,
    selected?: SelectedItemsType,
    setSelected?: (selected: SelectedItemsType) => void
) => {
    if (!setSelected || !selected) {
        return;
    }
    recursiveSelect(value, treeKey, treeNode, selected);
    setSelected(selected);
};

const onSelectEndpoint = (
    value: ThreeStateValueType,
    treeKey: string,
    treeNode: TreeNodeType,
    selected?: SelectedItemsType,
    setSelected?: (selected: SelectedItemsType) => void
) => {
    if (!selected) {
        return;
    }
    if (value) {
        selected[treeNode.data[treeKey]] = true;
    } else {
        delete selected[treeNode.data[treeKey]];
    }
    if (setSelected) {
        setSelected(selected);
    }
};

export interface SelectPropsType {
    treeNode: TreeNodeType;
    treeKey: string;
    selected?: SelectedItemsType;
    setSelected?: (selected: SelectedItemsType) => void;
    keysArr?: string[];
    disabled?: boolean;
    disabledItemsSelect?: SelectedItemsType;
    value?: ThreeStateValueType;
}

const Select: FC<SelectPropsType> = (props) => {
    const hasChildren = getChildKeys(props.treeNode).length > 0;
    const isLeafEndpoint = props.treeNode.isEndpoint && !hasChildren;

    if (isLeafEndpoint || !props.keysArr) {
        if (!props.selected) {
            return null;
        }
        return (
            <ThreeStateCheckbox
                value={props.value}
                onChange={(value) =>
                    onSelectEndpoint(value, props.treeKey, props.treeNode, { ...props.selected }, props.setSelected)
                }
                disabled={
                    props.disabled ||
                    (props.disabledItemsSelect && props.disabledItemsSelect[props.treeNode.data[props.treeKey]])
                }
            />
        );
    }

    return (
        <ThreeStateCheckbox
            value={props.value}
            onChange={(value) =>
                onSelectNode(value, props.treeKey, props.treeNode, { ...props.selected }, props.setSelected)
            }
            disabled={props.disabled}
        />
    );
};

export const getSelectValue = (params: {
    treeNode: TreeNodeType;
    treeKey: string;
    selected?: SelectedItemsType;
    selectedNodes: SelectedNodesType;
    keysArr?: string[];
}): ThreeStateValueType => {
    const hasChildren = getChildKeys(params.treeNode).length > 0;
    const isLeafEndpoint = params.treeNode.isEndpoint && !hasChildren;

    if (isLeafEndpoint || !params.keysArr) {
        if (!params.selected) {
            return 0;
        }
        return params.selected[params.treeNode.data[params.treeKey]] ? 1 : 0;
    }

    const nodeSelect = params.selectedNodes[params.keysArr?.join('.')];
    return nodeSelect?.[0] ? (nodeSelect[1] ? -1 : 1) : 0;
};

export default Select;
